/**
 * Today's install surfaces in one hook (docs/mockups/install.html, B + C + Desktop),
 * plus the offline strip they share a rule with ("never alongside another notice"):
 *
 * - `top`: under the brand row: the offline strip, or (in an app's own browser) the
 *   way-out card;
 * - `card`: B, Today's install line (or its confirmation): a ruled row under the big pill,
 *   never a second card competing with it (UX-029);
 * - `before`: the desktop Install button, for the brand row (the menu's install line is
 *   AppMenu's);
 * - `sheets`: whichever guide is open.
 *
 * Also prefetches tomorrow's daily in the evening (the service worker keeps it).
 */
import type { DateKey } from '@arrow/game-engine';
import { type ReactNode, useCallback, useEffect, useState } from 'react';

import { fetchDaily, fetchDailyLevels } from '../../api/client.ts';
import { installState, noteDismissed, noteSurfaceShown, saveCardDecision, useInstall } from '../../pwa/install.ts';
import { decideCard, desktopButtonVisible } from '../../pwa/install-state.ts';
import { useOnline } from '../../pwa/network.ts';
import { dayToPrefetch } from '../../pwa/prefetch.ts';
import { isControlled } from '../../pwa/register.ts';
import { Wedge } from '../icons.tsx';
import { Nib } from '../Nib.tsx';
import { useInstallFlow, WayOutCard } from './Install.tsx';
import { DesktopInstall, OfflineStrip } from './Surfaces.tsx';

export interface TodayInstall {
	top: ReactNode;
	card: ReactNode;
	before: ReactNode;
	sheets: ReactNode;
	/** ChallengeFeedback's line appeared: no install card alongside it. */
	onNotice: () => void;
	/** The install card was on Today this visit (shown, or turned down): no other ask after it. */
	asked: boolean;
}

export function useTodayInstall({ todayKey, dailiesCompleted }: { todayKey: DateKey; dailiesCompleted: number }): TodayInstall {
	const inst = useInstall();
	const online = useOnline();
	const p = inst.platform;
	const cardRoute = inst.route !== null && inst.route !== 'mac_safari' && !p.desktop;

	// The day's decision, made once per visit to Today (pure: StrictMode may ask twice).
	const [decision] = useState(() =>
		decideCard(installState(), {
			installed: inst.installed,
			dailiesCompleted,
			onToday: true,
			hasRoute: cardRoute,
			otherNotice: !online,
			today: todayKey,
		}),
	);
	const [cardOpen, setCardOpen] = useState(decision.show);
	useEffect(() => {
		saveCardDecision(decision.state);
		if (decision.show) noteSurfaceShown('today_card', todayKey);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	const hideCard = useCallback(() => setCardOpen(false), []);
	const cardFlow = useInstallFlow('today_card', hideCard);
	const deskFlow = useInstallFlow('desktop_button');

	useEffect(() => {
		if (!online) setCardOpen(false);
	}, [online]);

	usePrefetchTomorrow(todayKey);

	const state = installState();

	let top: ReactNode = null;
	let card: ReactNode = null;
	if (!online) top = <OfflineStrip />;
	else if (cardOpen && p.inApp) top = <WayOutCard platform={p} surface="today_card" onClose={() => (noteDismissed('today_card', 'not_now'), hideCard())} />;
	if (inst.installedNow && !p.desktop && online) card = <InstalledRow />;
	// getInstalledRelatedApps answers after the first render: if it finds Sparrow installed, the card goes.
	else if (cardOpen && online && !p.inApp && !inst.installed)
		card = (
			<InstallLine
				onAdd={cardFlow.start}
				onNotNow={() => {
					noteDismissed('today_card', 'not_now');
					hideCard();
				}}
			/>
		);

	const before = desktopButtonVisible(state, { installed: inst.installed, held: inst.held, desktop: p.desktop, dailiesCompleted, today: todayKey }) ? (
		<DesktopInstall onClick={deskFlow.start} />
	) : null;

	return {
		top,
		card,
		before,
		sheets: (
			<>
				{cardFlow.sheet}
				{deskFlow.sheet}
			</>
		),
		onNotice: hideCard,
		asked: decision.show,
	};
}

/**
 * Today's install line: Nib, the one line of selling, why, then "Add ›" and "Not now" as
 * quiet text buttons. A ruled row like Stats' install row, set under the big pill, so it
 * never competes with it (UX-029).
 */
function InstallLine({ onAdd, onNotNow }: { onAdd: () => void; onNotNow: () => void }) {
	return (
		<section className="tinst" aria-labelledby="tinst-t" data-testid="install-card">
			<Nib pose="idle" className="nb" />
			<div className="tinst-b">
				<h2 className="tinst-t" id="tinst-t">
					Add Sparrow to your home screen.
				</h2>
				<p className="tinst-s">Opens like an app.</p>
				<div className="tinst-acts">
					<button type="button" className="tlink" onClick={onAdd} aria-label="Add Sparrow to your home screen">
						Add
						<Wedge direction="right" />
					</button>
					<button type="button" className="tlink" onClick={onNotNow}>
						Not now
					</button>
				</div>
			</div>
		</section>
	);
}

/** The confirmation, on `appinstalled`: the same row, Nib cheering. */
function InstalledRow() {
	return (
		<section className="tinst ok" role="status" data-testid="install-ok">
			<Nib pose="cheer" className="nb" />
			<div className="tinst-b">
				<h2 className="tinst-t">Sparrow is on your home screen.</h2>
				<p className="tinst-s">Open it from there tomorrow.</p>
			</div>
		</section>
	);
}

let prefetched: DateKey | null = null;

/**
 * In the evening, quietly fetch tomorrow's levels and puzzles so the morning tap opens
 * instantly, even offline: the service worker caches them. Only once the api has
 * released tomorrow ({@link dayToPrefetch}); a 404 anyway (a clock that's off) is
 * swallowed, and the worker never caches it. Nothing here keeps or shows them; Today only
 * ever asks for the local date, so they're never seen early.
 */
function usePrefetchTomorrow(todayKey: DateKey) {
	useEffect(() => {
		if (prefetched === todayKey || !isControlled()) return;
		const tomorrow = dayToPrefetch(todayKey, new Date());
		if (!tomorrow) return;
		const t = window.setTimeout(() => {
			prefetched = todayKey;
			fetchDailyLevels(tomorrow)
				.then((l) => Promise.all(l.levels.map((x) => fetchDaily(tomorrow, x.level).catch(() => null))))
				.catch(() => null);
		}, 2500);
		return () => window.clearTimeout(t);
	}, [todayKey]);
}
